(function exposeSolveBridgeTankCore(root) {
  "use strict";

  const dimensionToMetres = Object.freeze({
    mm: 0.001,
    cm: 0.01,
    m: 1,
    in: 0.0254,
    ft: 0.3048,
  });

  const flowToLitresPerMinute = Object.freeze({
    lpm: 1,
    lps: 60,
    lph: 1 / 60,
    m3h: 1000 / 60,
    usgpm: 3.785411784,
    ukgpm: 4.54609,
  });

  function convert(value, unit, factors, label) {
    const factor = factors[unit];
    if (!factor) throw new RangeError(`Unsupported ${label} unit: ${unit}`);
    return Number(value) * factor;
  }

  function toMetres(value, unit) {
    return convert(value, unit, dimensionToMetres, "dimension");
  }

  function toLitresPerMinute(value, unit) {
    return convert(value, unit, flowToLitresPerMinute, "flow");
  }

  function calculateTank({
    shape,
    length,
    lengthUnit,
    width,
    widthUnit,
    diameter,
    diameterUnit,
    height,
    heightUnit,
    currentLevel = 0,
    currentLevelUnit = heightUnit,
    flowRate,
    flowUnit = "lpm",
  }) {
    const heightM = toMetres(height, heightUnit);
    const currentLevelM = toMetres(currentLevel || 0, currentLevelUnit);

    if (!Number.isFinite(heightM) || heightM <= 0) {
      throw new RangeError("Tank height must be greater than zero.");
    }
    if (!Number.isFinite(currentLevelM) || currentLevelM < 0 || currentLevelM > heightM) {
      throw new RangeError("Current water level must be between zero and tank height.");
    }

    let baseAreaM2;
    if (shape === "rectangular") {
      const lengthM = toMetres(length, lengthUnit);
      const widthM = toMetres(width, widthUnit);
      if (![lengthM, widthM].every((value) => Number.isFinite(value) && value > 0)) {
        throw new RangeError("Length and width must be greater than zero.");
      }
      baseAreaM2 = lengthM * widthM;
    } else if (shape === "cylindrical") {
      const diameterM = toMetres(diameter, diameterUnit);
      if (!Number.isFinite(diameterM) || diameterM <= 0) {
        throw new RangeError("Diameter must be greater than zero.");
      }
      baseAreaM2 = Math.PI * (diameterM / 2) ** 2;
    } else {
      throw new RangeError(`Unsupported tank shape: ${shape}`);
    }

    const totalLitres = baseAreaM2 * heightM * 1000;
    const currentLitres = baseAreaM2 * currentLevelM * 1000;
    const remainingLitres = Math.max(0, totalLitres - currentLitres);
    const fillPercent = (currentLitres / totalLitres) * 100;
    let fillMinutes = null;

    if (flowRate !== "" && flowRate !== null && flowRate !== undefined) {
      const flowLitresPerMinute = toLitresPerMinute(flowRate, flowUnit);
      if (!Number.isFinite(flowLitresPerMinute) || flowLitresPerMinute <= 0) {
        throw new RangeError("Flow rate must be greater than zero.");
      }
      fillMinutes = remainingLitres / flowLitresPerMinute;
    }

    return {
      totalLitres,
      currentLitres,
      remainingLitres,
      fillPercent,
      fillMinutes,
      fullWaterMassKg: totalLitres,
    };
  }

  function measuredFlowLitresPerMinute(bucketLitres, seconds) {
    const volume = Number(bucketLitres);
    const duration = Number(seconds);
    if (!Number.isFinite(volume) || volume <= 0 || !Number.isFinite(duration) || duration <= 0) {
      throw new RangeError("Bucket volume and time must be greater than zero.");
    }
    return volume * 60 / duration;
  }

  root.SolveBridgeTankCore = Object.freeze({
    calculateTank,
    measuredFlowLitresPerMinute,
    toLitresPerMinute,
    toMetres,
  });
}(globalThis));
